import { faker } from '@faker-js/faker';
import { environment } from '@environments/environment';
import {
  CetoineSubType,
  ChondrusSubType,
  ENVIRONMENTS,
  NixSubType,
  OfferType,
  RETAILERS,
  ScorpioSubType,
  SubType,
} from '@shared/constants';
import { ProductScopeDTO } from '@shared/dto';
import { Step } from '@shared/factory';

import {
  brandBannerPath,
  brandLogoPath,
  DemoBrand,
  FALLBACK_BANNER_PATH,
  FALLBACK_LOGO_PATH,
  pickRealBrand,
  USE_REAL_BRANDS,
} from './brands.mock';

export class MockChallengeBase {
  offerId: number;
  rank: number = faker.number.int(30);
  type: OfferType = OfferType.STEP;
  sub_type: SubType = SubType.DEFAULT;
  selected_date = '';
  progress = 0;
  reward = 0;
  remaining_selections: number = 10000;

  constructor(
    offerId: number,
    isSelected = false,
    progress = 0,
    subType: SubType = SubType.DEFAULT
  ) {
    this.offerId = offerId;
    this.sub_type = subType;
    if (isSelected) {
      this.progress = progress;
      this.selected_date = faker.date.recent().toISOString();
    }
  }
}

export class MockStepChallenge extends MockChallengeBase {
  steps: Step[] = [];
  drive_link: string;
  product_scope: ProductScopeDTO = {
    logo_path: '',
    name: faker.company.name(),
    label: {
      en: faker.commerce.productDescription(),
      fr: faker.commerce.productDescription(),
      es: faker.commerce.productDescription(),
    },
    product_scope_id: faker.number.int(1000),
    description: {
      en: faker.commerce.productDescription(),
      fr: faker.commerce.productDescription(),
      es: faker.commerce.productDescription(),
    },
  };

  constructor(
    offerId: number,
    isSelected = false,
    progress = 0,
    withLogo = true,
    withBanner = true,
    subType: SubType = SubType.DEFAULT
  ) {
    super(offerId, isSelected, progress, subType);
    for (let i = 0; i < 3; i++) {
      const target: number = (i + 1) * 15;
      const reward: number = (i + 1) * 125;
      this.steps.push({ target: target, reward: reward });
      if (progress >= target) {
        this.reward = reward;
      }
    }

    // Demo mode: dress the card with a real grocery brand (name + scope text +
    // optional own logo/banner). Keyed on offerId so it stays stable. Disabled
    // under Playwright so the visual tests keep their fixed fixture.
    const brand: DemoBrand | null =
      USE_REAL_BRANDS && environment.env !== ENVIRONMENTS.PLAYWRIGHT
        ? pickRealBrand(offerId)
        : null;

    if (environment.env === ENVIRONMENTS.PLAYWRIGHT) {
      this.product_scope.label['en'] = 'Beverages';
      this.product_scope.description['en'] =
        'All beverages including soda, water, juice, and much more, excluding all alcoholic beverages., except the ones with a high sugar content.';
    } else if (brand) {
      this.product_scope.name = brand.name;
      this.product_scope.label = { ...brand.label };
      this.product_scope.description = { ...brand.description };
    }
    if (withLogo) {
      this.product_scope.logo_path = brand
        ? brandLogoPath(brand)
        : FALLBACK_LOGO_PATH;
    }
    // A brand that ships its own banner always shows it (so every branded card
    // is recognisable in demos); otherwise keep the alternating banner/logo mix.
    if (withBanner || brand?.bannerPath) {
      this.product_scope.banner_path = brand
        ? brandBannerPath(brand)
        : FALLBACK_BANNER_PATH;
    }
  }
}

export class MockFrequencyChallenge extends MockChallengeBase {
  target = 5;
  reward_ratio = 3;
  min_amount_by_transaction = 2;

  constructor(
    offerId: number,
    isSelected = false,
    progress = 0,
    subType: SubType = SubType.DEFAULT
  ) {
    super(offerId, isSelected, progress, subType);
    this.type = OfferType.FREQUENCY;
    if (progress >= this.target) {
      this.reward = this.reward_ratio;
    }
  }
}

export class MockReferralChallenge extends MockChallengeBase {
  steps: Step[] = [];
  code: string = faker.string.alphanumeric(8);
  nb_pending_sponsorships: number = faker.number.int(10);

  constructor(offerId: number, isSelected = false, progress = 0) {
    super(offerId, isSelected, progress);
    this.type = OfferType.SPONSOR;
    for (let i = 0; i < 3; i++) {
      const target: number = i + 1;
      const reward: number = (i + 1) * 2;
      this.steps.push({ target: target, reward: reward });
      if (progress >= target) {
        this.reward = reward;
      }
    }
  }
}

export class MockSelectToWinChallenge extends MockChallengeBase {
  prize = 5;
  constructor(offerId: number, isSelected = false) {
    super(offerId, isSelected, 0);
    this.type = OfferType.SELECT_TO_WIN;
    if (isSelected) {
      this.reward = this.prize;
    }
  }
}

export type MockAnyChallenge =
  | MockStepChallenge
  | MockFrequencyChallenge
  | MockReferralChallenge
  | MockSelectToWinChallenge
  | MockChallengeBase;

export interface ChallengeTypeParams {
  nbToGenerate: number;
  nbSelected: number;
  progress: number;
  challengeType?: OfferType;
  subType?: SubType;
}

export class FullResponseParams {
  step: ChallengeTypeParams = {
    nbToGenerate: 17,
    nbSelected: 6,
    progress: 44.9,
  };
  frequency: ChallengeTypeParams = {
    nbToGenerate: 2,
    nbSelected: 1,
    progress: 2,
  };
  sponsor: ChallengeTypeParams = {
    nbToGenerate: 1,
    nbSelected: 1,
    progress: 2,
  };
  event: ChallengeTypeParams = {
    nbToGenerate: 0,
    nbSelected: 0,
    progress: 0,
    challengeType:
      OfferType.SELECT_TO_WIN || OfferType.FREQUENCY || OfferType.STEP,
  };

  constructor(
    status:
      | 'default'
      | 'noSelections'
      | 'oneSelectionLeft'
      | 'allSelected'
      | 'allWon' = 'default',
    onlyEvent = false,
    withSponsor = true
  ) {
    const envType: ENVIRONMENTS = environment.env;
    if (status === 'noSelections') {
      for (const value of Object.values(this)) {
        value.nbSelected = 0;
        value.progress = 0;
      }
    } else if (status === 'oneSelectionLeft') {
      this.step.nbSelected = 7;
    } else if (status === 'allSelected') {
      this.step.nbSelected = 8;
    } else if (status === 'allWon') {
      this.step.progress = 50;
      this.frequency.progress = 8;
      this.sponsor.progress = 4;
    }
    if (onlyEvent) {
      for (const value of Object.values(this)) {
        value.nbToGenerate = 0;
        value.nbSelected = 0;
        value.progress = 0;
      }
      this.event.nbToGenerate = 1;
      let retailerName = environment.retailerName;
      if ([ENVIRONMENTS.PLAYWRIGHT, ENVIRONMENTS.CYPRESS].includes(envType)) {
        retailerName = localStorage.getItem('retailerName') as RETAILERS;
      }

      this.event.challengeType =
        retailerName === RETAILERS.CETOINE
          ? OfferType.STEP
          : OfferType.SELECT_TO_WIN;
    }
    if (!withSponsor) {
      this.sponsor.nbToGenerate = 0;
      this.sponsor.nbSelected = 0;
      this.sponsor.progress = 0;
    }
  }
}

export const generateChallenges: (
  params?: FullResponseParams,
  isBisCampaign?: boolean
) => Array<MockAnyChallenge> = (
  params: FullResponseParams = new FullResponseParams(),
  isBisCampaign = false
): Array<MockAnyChallenge> => {
  const challenges: MockAnyChallenge[] = [];
  let offerId = 1000;
  let retailerName = environment.retailerName;
  if (
    [ENVIRONMENTS.PLAYWRIGHT, ENVIRONMENTS.CYPRESS].includes(environment.env)
  ) {
    retailerName = localStorage.getItem('retailerName') as RETAILERS;
  }
  if (isBisCampaign) {
    offerId = 2000;
  }
  if (params.step) {
    const nbChallenges: number = params.step.nbToGenerate;
    // Alternate the retailer sub types so every design is visible locally.
    // Where that design shows depends on the retailer: on the bonus (bis) pages
    // for one that runs a bonus campaign (chondrus), on the standard cards for
    // one that does not (scorpio) — see `StepChallengeComponent.subType`.
    // Kept to DEFAULT under Playwright so the visual tests keep their fixture,
    // unless the spec forces one through `params.step.subType`.
    //
    // Only sub types the retailer's step config actually defines are listed: a
    // value with no config falls back to the default design and would just
    // waste a slot in the rotation.
    const stepSubTypesByRetailer: Partial<Record<RETAILERS, SubType[]>> = {
      [RETAILERS.CHONDRUS]: Object.values(ChondrusSubType) as SubType[],
      [RETAILERS.SCORPIO]: [ScorpioSubType.KOPITIAM],
    };
    const availableStepSubTypes: SubType[] = [
      SubType.DEFAULT,
      ...(environment.env === ENVIRONMENTS.PLAYWRIGHT
        ? []
        : (stepSubTypesByRetailer[retailerName] ?? [])),
    ];
    for (let i = 0; i < nbChallenges; i++) {
      const isSelected: boolean = i < params.step.nbSelected;
      const progress: number = isSelected
        ? i === 6
          ? 0
          : params.step.progress
        : 0;
      const withBanner: boolean = i % 2 === 0;
      const subType: SubType =
        params.step.subType ??
        availableStepSubTypes[i % availableStepSubTypes.length];
      challenges.push(
        new MockStepChallenge(
          offerId,
          isSelected,
          progress,
          true,
          withBanner,
          subType
        )
      );
      offerId++;
    }
  }
  if (params.frequency) {
    const generationParams: ChallengeTypeParams = params.frequency;
    const nbChallenges: number = generationParams.nbToGenerate;
    const subTypesByRetailer: Partial<Record<RETAILERS, SubType[]>> = {
      [RETAILERS.CETOINE]: Object.values(CetoineSubType) as SubType[],
      [RETAILERS.NIX]: Object.values(NixSubType) as SubType[],
      [RETAILERS.SCORPIO]: Object.values(ScorpioSubType) as SubType[],
    };
    const availableSubTypes: SubType[] = [
      SubType.DEFAULT,
      ...(subTypesByRetailer[retailerName] ?? []),
    ];
    for (let i = 0; i < nbChallenges; i++) {
      const isSelected: boolean = i < generationParams.nbSelected;
      const subType: SubType =
        generationParams.subType ??
        availableSubTypes[i % availableSubTypes.length];
      challenges.push(
        new MockFrequencyChallenge(
          offerId,
          isSelected,
          generationParams.progress,
          subType
        )
      );
      offerId++;
    }
  }
  if (params.sponsor) {
    const generationParams: ChallengeTypeParams = params.sponsor;
    const nbChallenges: number = generationParams.nbToGenerate;
    for (let i = 0; i < nbChallenges; i++) {
      const isSelected: boolean = i < generationParams.nbSelected;
      challenges.push(
        new MockReferralChallenge(
          offerId,
          isSelected,
          generationParams.progress
        )
      );
      offerId++;
    }
  }
  if (params.event) {
    const eventParams: ChallengeTypeParams = params.event;
    const nbChallenges: number = eventParams.nbToGenerate;
    for (let i = 0; i < nbChallenges; i++) {
      const isSelected: boolean = i < eventParams.nbSelected;
      if (eventParams.challengeType === OfferType.SELECT_TO_WIN) {
        challenges.push(new MockSelectToWinChallenge(offerId, isSelected));
      } else if (eventParams.challengeType === OfferType.FREQUENCY) {
        const subType: SubType = SubType.DEFAULT;
        challenges.push(
          new MockFrequencyChallenge(
            offerId,
            isSelected,
            eventParams.progress,
            subType
          )
        );
      } else if (eventParams.challengeType === OfferType.STEP) {
        const progress: number = isSelected
          ? i === 6
            ? 0
            : params.step.progress
          : 0;
        const withBanner: boolean = i % 2 === 0;
        challenges.push(
          new MockStepChallenge(offerId, isSelected, progress, true, withBanner)
        );
      }
      offerId++;
    }
  }
  return challenges;
};
